import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2 } from "lucide-react";
import Mandala from "@/components/Mandala";

/**
 * BookingSuccess renders for two routes:
 *  • /booking/success?session_id=...      (Stripe redirect — polls status)
 *  • /booking/:id/confirmed               (legacy / direct link)
 */
export default function BookingSuccess() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [phase, setPhase] = useState(sessionId ? "polling" : "ready");
  const [booking, setBooking] = useState(null);
  const pollRef = useRef(0);

  // Stripe redirect: poll backend until payment_status === "paid" or expired/timeout.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const tick = async () => {
      pollRef.current += 1;
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          if (data.booking_id) {
            const { data: mine } = await api.get("/bookings/me");
            setBooking(mine.find((b) => b.id === data.booking_id) || mine[0] || null);
          }
          setPhase("paid");
          return;
        }
        if (data.status === "expired") { setPhase("expired"); return; }
        if (pollRef.current >= 12) { setPhase("timeout"); return; }
        setTimeout(tick, 2000);
      } catch {
        if (pollRef.current >= 12) setPhase("timeout");
        else setTimeout(tick, 2500);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  // Legacy direct booking confirmation
  useEffect(() => {
    if (sessionId || !id) return;
    (async () => {
      try {
        const { data } = await api.get("/bookings/me");
        setBooking(data.find((b) => b.id === id) || null);
      } catch {}
    })();
  }, [id, sessionId]);

  if (phase === "polling") {
    return (
      <div className="pt-32 pb-24 min-h-[80vh] flex items-center" data-testid="booking-polling">
        <div className="container-aviana max-w-2xl text-center">
          <Mandala size={80} variant="full" className="text-aviana-gold mx-auto mb-6 animate-spin-slow" />
          <p className="overline mb-3">Sealing your reservation</p>
          <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">Confirming your payment…</h1>
          <p className="mt-6 text-aviana-muted">A moment of breath. Please don't close this page.</p>
          <Loader2 size={20} className="text-aviana-gold mx-auto mt-6 animate-spin" />
        </div>
      </div>
    );
  }

  if (phase === "expired" || phase === "timeout") {
    return (
      <div className="pt-32 pb-24 min-h-[80vh] flex items-center" data-testid="booking-issue">
        <div className="container-aviana max-w-2xl text-center">
          <p className="overline mb-3">Hmm</p>
          <h1 className="font-display text-4xl md:text-5xl text-aviana-ink">
            {phase === "expired" ? "That checkout expired." : "Still waiting on Stripe…"}
          </h1>
          <p className="mt-6 text-aviana-muted">
            {phase === "expired"
              ? "No charge was made. Please return to the offering page to try again."
              : "Your card may have been charged. Check your email for a Stripe receipt — if you have one, your booking will appear in your dashboard shortly."}
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/dashboard" className="btn-primary">View dashboard</Link>
            <Link to="/" className="btn-secondary">Return home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 min-h-[80vh] flex items-center" data-testid="booking-success-page">
      <div className="container-aviana max-w-2xl text-center">
        <div className="relative mx-auto w-24 h-24 mb-6">
          <Mandala size={96} variant="full" className="text-aviana-gold absolute inset-0" />
          <CheckCircle2 size={32} className="text-aviana-plum absolute inset-0 m-auto" />
        </div>
        <p className="overline mb-3">You're in.</p>
        <h1 className="font-display text-5xl md:text-6xl text-aviana-ink">Your spot is reserved.</h1>
        {booking && (
          <p className="mt-6 text-lg text-aviana-muted">
            <span className="text-aviana-ink font-medium">{booking.offering_title}</span>
            <br />
            ${booking.amount_paid_usd.toLocaleString()} paid
            {booking.payment_type === "deposit" && ` · Balance of $${(booking.total_due_usd - booking.amount_paid_usd).toLocaleString()} due before arrival`}
          </p>
        )}
        <p className="mt-6 text-sm text-aviana-muted">
          A Stripe receipt is on its way to your inbox. We can't wait to hold space for you.
        </p>
        <div className="mt-10 flex flex-wrap gap-4 justify-center">
          <Link to="/dashboard" className="btn-primary" data-testid="success-dashboard-link">View my dashboard</Link>
          <Link to="/" className="btn-secondary">Return home</Link>
        </div>
      </div>
    </div>
  );
}
